import { MlpPageServiceResponse, organizerPageService } from '@/cmsBackend';
import {
  getEnvDataFromContext,
  getHostnameFromStageAndTLD,
  getUserDataFromContext,
  isPagePreviewRequest,
} from '@/common/helpers/requestHelpers';
import { serve404Page, setCommonPageResponseHeaders } from '@/common/helpers/responseHelpers';
import { getCMSLocaleFromServerLocale } from '@/common/helpers/uiHelpers';
import { Env } from '@/common/types';
import { HeadSEO } from '@/components/seo/HeadSEO';
import { TAG_MANAGER_ENVIRONMENT } from '@/constants/tagManager';
import { OrganizerContainer } from '@/containers/OrganizerContainer';
import { TagManager } from '@eventbrite/webapp-data';
import { GetServerSideProps, NextPage } from 'next';

interface ServerData {
  env: Env;
  user: {
    // send only userId for TagManager
    userId: string;
  };
  cmsResponse: MlpPageServiceResponse;
}

/**
 * Server Side Render (SSR) the /organizer/* pages
 *
 * Extracts Env data based on Request - Slug, TLD, Locale.
 * Fetch CMS data using organizerPageService.
 * Handles rendering of Organizer Page UI.
 * Handles rendering of 404 and 5xx Page.
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;

  // Get Env and User data from request context
  const env = getEnvDataFromContext(context);
  const user = getUserDataFromContext(context);
  const { slug, stage, tld, locale, pagePath } = env;
  const requestedPage = getHostnameFromStageAndTLD(stage, tld) + pagePath;
  const isPreview = Boolean(query.is_preview);

  console.log(`[OrganizerPage]: ${requestedPage} | `, env);

  const response = await organizerPageService({
    requestedPage,
    slug,
    locale: getCMSLocaleFromServerLocale(locale),
    is_preview: isPreview,
  });

  // check response for errors
  const is5xx = response.isError;
  const is404 = !Object.keys(response.data).length;

  // Set response Headers only after service call and before sending page response
  const isError = is5xx || is404;
  const isPreviewRequest = isPagePreviewRequest(context.req, isPreview);
  setCommonPageResponseHeaders(context.res, user, isPreviewRequest, isError);

  // Always check for 5xx status first
  if (is5xx) {
    context.res.statusCode = 500;
    // This triggers _error.page.tsx and handles with fallback UI
    throw new Error('[OrganizerPage] Error in props.cmsResponse');
  }

  if (is404) {
    // 404 page for MLP data not found in CMS
    return serve404Page();
  }

  // OrganizerPage Props
  const pageProps: ServerData = {
    env,
    user: {
      userId: user.userId,
    },
    cmsResponse: response,
  };

  return {
    props: pageProps,
  };
};

/**
 * Page renders the Container that builds UI
 */
const OrganizerPage: NextPage<ServerData> = (props) => {
  // Extract pageData from service response
  const pageData = props.cmsResponse.data;

  return (
    <>
      <HeadSEO {...pageData.seo} />
      <OrganizerContainer env={props.env} pageData={pageData} />
      <TagManager
        publicUserId={props.user.userId}
        stage={props.env.stage}
        environment={TAG_MANAGER_ENVIRONMENT}
      />
    </>
  );
};

export default OrganizerPage;
